import { useState } from "react";
import type { ReactNode } from "react";
import { useSession } from "../../lib/session";
import { Button } from "../ui/primitives";
import { AuthModal } from "./AuthModal";

/**
 * Wraps a personal screen (library/collections/stats). Signed-in users see the
 * content; anonymous users get a prompt to sign in (they can still use the rest
 * of the app — this only gates the saving surfaces).
 */
export function AuthedPage({ title, children }: { title: string; children: ReactNode }) {
  const { user, ready } = useSession();
  const [authOpen, setAuthOpen] = useState(false);

  if (!ready) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-6 py-24 text-center">
        <h1 className="text-xl font-semibold text-zinc-100">{title}</h1>
        <p className="text-sm text-zinc-500">Sign in to build and view your {title.toLowerCase()}.</p>
        <Button variant="primary" onClick={() => setAuthOpen(true)}>
          Sign in
        </Button>
        {authOpen && <AuthModal onClose={() => setAuthOpen(false)} />}
      </div>
    );
  }

  return <div className="mx-auto max-w-5xl px-6 py-8">{children}</div>;
}
